import { useNavigate } from "react-router-dom";
import { ArrowRight, Headphones, Brain, Heart, Sparkles, ShieldCheck } from "lucide-react";
import useSeo from "../hooks/useSeo";
import { PublicHeader } from "../components/PublicHeader";

// ---------------------------------------------------------------------------
// Long-form "About" / pillar page — main SEO long-tail magnet for queries
// like "what is bilateral sound", "EMDR self-help app", "bilateral
// stimulation for sleep". Optimised for ~1,400 words, h2/h3 hierarchy,
// dense use of target keywords, internal links to /pricing, /faq,
// /sign-in. JSON-LD Article block at the bottom of the rendered output is
// emitted via useSeo's <meta> overrides + the page's own inline JSON.
// ---------------------------------------------------------------------------

const ARTICLE_LD = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline":
    "What is bilateral sound? A complete guide to bilateral stimulation and EMDR for anxiety, insomnia, stress, and trauma",
  "description":
    "Bilateral sound is the alternating left-right audio pattern at the heart of EMDR (Eye Movement Desensitization and Reprocessing). Here's how it works, what it helps with, and how Bilateral Calm makes it accessible at home.",
  "author": { "@type": "Organization", "name": "Bilateral Calm" },
  "publisher": {
    "@type": "Organization",
    "name": "Bilateral Calm",
    "logo": {
      "@type": "ImageObject",
      "url": "https://bilateralcalm.com/logo-bilateral-calm.png",
    },
  },
  "image": "https://bilateralcalm.com/images/home-hero-reeds.webp",
  "mainEntityOfPage": "https://bilateralcalm.com/about",
};

const AboutPage = () => {
  const navigate = useNavigate();

  useSeo({
    title:
      "What is Bilateral Sound? A Complete Guide to EMDR Self-Help | Bilateral Calm",
    description:
      "Bilateral sound is the alternating left-right audio used in EMDR (Eye Movement Desensitization and Reprocessing). Learn how it works for anxiety, insomnia, stress, and trauma — and how Bilateral Calm brings it home.",
    path: "/about",
  });

  return (
    <div className="min-h-screen bg-[#FBF8F1]" data-testid="about-page">
      {/* Inline Article JSON-LD for Google rich-result eligibility. */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ARTICLE_LD) }}
      />

      <PublicHeader />

      {/* Hero */}
      <section className="px-6 sm:px-10 max-w-3xl mx-auto pt-8 pb-12 text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-[#3090A8] font-medium mb-4">
          A complete guide
        </p>
        <h1 className="font-outfit font-semibold text-4xl sm:text-5xl lg:text-6xl text-[#1B2349] tracking-tight leading-[1.05]">
          What is bilateral sound?
        </h1>
        <p className="mt-6 text-lg sm:text-xl text-[#4A5568] leading-relaxed">
          The science behind bilateral stimulation and EMDR — and how
          Bilateral Calm brings it home for anxiety, insomnia, stress, and
          trauma relief, without a clinic visit.
        </p>
      </section>

      {/* Body — dense long-form copy for SEO + reader value */}
      <article
        className="px-6 sm:px-10 max-w-3xl mx-auto pb-16 prose prose-slate text-[#1B2349]"
        data-testid="about-article"
      >
        <section className="space-y-4 leading-relaxed">
          <h2 className="font-outfit font-semibold text-2xl sm:text-3xl mt-6 mb-3 text-[#1B2349]">
            Bilateral sound, in one sentence
          </h2>
          <p>
            <strong>Bilateral sound</strong> is audio that alternates back and
            forth between your left and right ears — gently, rhythmically, on
            purpose. It's a form of <strong>bilateral stimulation</strong>,
            the same active ingredient at the heart of a clinically
            recognised therapy: <strong>EMDR</strong> (Eye Movement
            Desensitization and Reprocessing).
          </p>
          <p>
            Where traditional meditation audio asks you to passively follow a
            single voice or ambient soundscape, bilateral sound asks your
            <em> nervous system</em> to track a left-right pattern. That
            pattern engages both hemispheres of the brain, and over the
            course of a short session it tends to soften the body's
            fight-or-flight response — which is why bilateral stimulation
            became central to trauma therapy in the first place.
          </p>
        </section>

        <section className="space-y-4 leading-relaxed">
          <h2 className="font-outfit font-semibold text-2xl sm:text-3xl mt-10 mb-3 text-[#1B2349]">
            How bilateral stimulation works — the short version
          </h2>
          <p>
            EMDR therapists call this technique "dual attention stimulation."
            The client holds a difficult memory or feeling in mind while
            following the therapist's finger left-to-right (or tapping their
            knees alternately, or listening to alternating tones in
            headphones). The dual focus — feeling the thing <em>and</em>{" "}
            tracking the rhythm — appears to help the brain re-file the
            memory in a less distressing way.
          </p>
          <p>
            EMDR has a substantial body of clinical research behind it,
            particularly for <strong>PTSD</strong>,{" "}
            <strong>anxiety disorders</strong>, and{" "}
            <strong>complex trauma</strong>. Studies consistently show that
            the alternating left-right stimulation — whether via eye
            movements, tapping, or tones in headphones — helps the brain
            re-process difficult memories with less distress.
          </p>
          <p>
            You don't need a clinical setting to feel some of the calming
            effects of bilateral stimulation. Lying down with headphones and
            following an alternating left-right tone is the most accessible
            entry point — and it's exactly what Bilateral Calm guides you
            through.
          </p>
        </section>

        <section className="space-y-4 leading-relaxed">
          <h2 className="font-outfit font-semibold text-2xl sm:text-3xl mt-10 mb-3 text-[#1B2349]">
            What bilateral sound feels like
          </h2>
          <p>
            Headphones are essential — the effect depends on stereo
            separation. When you start a Bilateral Calm session, you'll
            notice the tone moving back and forth: left, right, left, right.
            The pace is slow at first (about a beat per second), then settles
            into whatever pattern best matches the practice you've chosen.
          </p>
          <p>
            Most people report two things within five minutes:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-1">
            <li>
              A noticeable drop in racing thoughts — the bilateral rhythm
              "gives the mind something to do" that isn't worrying.
            </li>
            <li>
              A subtle physical settling — slower breathing, softer
              shoulders, less stomach tension.
            </li>
          </ul>
          <p>
            Some users also describe a quiet, almost dreamlike state in
            longer sessions. That's part of why bilateral stimulation is so
            useful for <strong>insomnia</strong>: it gives the racing nighttime
            mind a soothing alternative track to follow.
          </p>
        </section>

        <section className="space-y-4 leading-relaxed">
          <h2 className="font-outfit font-semibold text-2xl sm:text-3xl mt-10 mb-3 text-[#1B2349]">
            Why we built Bilateral Calm
          </h2>
          <p>
            Therapy access is uneven. A meaningful EMDR intake can take weeks
            to schedule and cost hundreds of dollars per session. Plenty of
            people who would benefit from bilateral stimulation simply never
            get the chance to try it.
          </p>
          <p>
            Bilateral Calm is a <strong>self-help app</strong> designed by
            people who care deeply about that gap. It pairs carefully
            engineered <strong>bilateral sound</strong> with structured
            9-step guided practices for the everyday emotional experiences
            most people face: anxiety, the fear of sleeplessness, stress and
            burnout, body tension, low self-worth, grief, anger, stage
            fright, and habit-building.
          </p>
          <p>
            Crucially: Bilateral Calm is <em>not</em> a substitute for
            therapy. If you're actively working through trauma or dealing
            with serious mental health challenges, please work with a
            licensed mental health professional. Bilateral Calm sits
            alongside that work as a daily, accessible regulation tool — or
            as a gentle entry point for people who aren't yet ready for the
            clinical setting.
          </p>
        </section>

        <section className="space-y-4 leading-relaxed">
          <h2 className="font-outfit font-semibold text-2xl sm:text-3xl mt-10 mb-3 text-[#1B2349]">
            Inside the app
          </h2>
          <h3 className="font-outfit font-medium text-xl mt-6 mb-2 text-[#1B2349]">
            Structured practices
          </h3>
          <p>
            Thirteen guided practices, each built as a 9-step flow that pairs
            bilateral sound with short, concrete tasks. You pick the one
            that matches what you're feeling — "Quiet the worry," "Ease
            insomnia," "Soften body tension," "Heal a painful story," and so
            on — and the app walks you through it at your own pace.
          </p>
          <h3 className="font-outfit font-medium text-xl mt-6 mb-2 text-[#1B2349]">
            Daily Practices audio library
          </h3>
          <p>
            A separate background-friendly library for ongoing nervous-system
            regulation: deep-calm 432 Hz, gamma 40 Hz, ocean waves, and
            forest tones, all engineered with bilateral stereo separation so
            they keep working even when you're not actively in a guided
            session.
          </p>
          <h3 className="font-outfit font-medium text-xl mt-6 mb-2 text-[#1B2349]">
            Privacy by design
          </h3>
          <p>
            We don't sell data and we don't run ads. Your practice history is
            yours — exportable and deletable at any time from the privacy
            page. Read more in our{" "}
            <button
              onClick={() => navigate("/privacy")}
              className="underline text-[#3090A8] hover:text-[#1B2349]"
              data-testid="about-privacy-link"
            >
              privacy policy
            </button>
            .
          </p>
        </section>

        <section className="space-y-4 leading-relaxed">
          <h2 className="font-outfit font-semibold text-2xl sm:text-3xl mt-10 mb-3 text-[#1B2349]">
            Common questions
          </h2>
          <p>
            For specific FAQs — including the difference between bilateral
            sound and regular meditation audio, whether Bilateral Calm helps
            with insomnia, and how it compares to EMDR therapy — see
            our{" "}
            <button
              onClick={() => navigate("/faq")}
              className="underline text-[#3090A8] hover:text-[#1B2349]"
              data-testid="about-faq-link"
            >
              FAQ page
            </button>
            .
          </p>
        </section>

        {/* CTA */}
        <section className="mt-12">
          <div
            className="rounded-3xl bg-white border border-slate-100 p-8 sm:p-10 text-center shadow-[0_14px_44px_rgba(0,0,0,0.04)]"
            data-testid="about-cta-card"
          >
            <h2 className="font-outfit font-semibold text-2xl sm:text-3xl text-[#1B2349] mb-3">
              Try bilateral sound for yourself
            </h2>
            <p className="text-[#4A5568] mb-6 leading-relaxed max-w-xl mx-auto">
              Five days free. No card required to begin. Lie down with
              headphones, pick a practice, and let your nervous system find
              its way back to calm.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate("/signup")}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#1B2349] text-white font-medium hover:bg-[#0F1838] transition-all duration-200 active:scale-[0.98]"
                data-testid="about-cta-trial-btn"
              >
                Start your free trial
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate("/pricing")}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white border border-[#1B2349]/15 text-[#1B2349] font-medium hover:border-[#1B2349]/40 hover:bg-[#FBF6EE] transition-all duration-200 active:scale-[0.98]"
                data-testid="about-cta-pricing-btn"
              >
                See plans
              </button>
            </div>
          </div>
        </section>
      </article>

      {/* Footer */}
      <footer className="border-t border-slate-100 mt-4 py-8">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 flex flex-wrap gap-4 items-center justify-between text-sm text-[#718096]">
          <span>© Bilateral Calm</span>
          <div className="flex gap-6">
            <button onClick={() => navigate("/pricing")} className="hover:text-[#1B2349]" data-testid="about-footer-pricing">Plans</button>
            <button onClick={() => navigate("/faq")} className="hover:text-[#1B2349]" data-testid="about-footer-faq">FAQ</button>
            <button onClick={() => navigate("/privacy")} className="hover:text-[#1B2349]" data-testid="about-footer-privacy">Privacy</button>
            <button onClick={() => navigate("/sign-in")} className="hover:text-[#1B2349]" data-testid="about-footer-signin">Sign in</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
